import React, { Suspense } from 'react';

// Trading-specific loading components
export const TradingDataLoader = ({ children, fallback, type = 'data' }) => {
  const defaultFallback = (
    <div className="trading-data-loader">
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h6 className="text-muted">Loading {type}...</h6>
          <p className="small text-muted">Fetching latest market data</p>
        </div>
      </div>
    </div>
  );

  return (
    <Suspense fallback={fallback || defaultFallback}>
      {children}
    </Suspense>
  );
};

// Option chain specific loading
export const OptionChainLoader = ({ children, selectedIndex, selectedExpiry }) => {
  const fallback = (
    <div className="option-chain-loader">
      <div className="card">
        <div className="card-header bg-primary text-white">
          <h5 className="card-title mb-0">
            <i className="fas fa-chart-line me-2"></i>
            Home
          </h5>
        </div>
        <div className="card-body">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
            <div className="text-center">
              <div className="spinner-grow text-primary mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <h6>Loading Home</h6>
              {selectedIndex && (
                <p className="text-muted mb-1">
                  <strong>Index:</strong> {selectedIndex}
                </p>
              )}
              {selectedExpiry && (
                <p className="text-muted mb-1">
                  <strong>Expiry:</strong> {selectedExpiry}
                </p>
              )}
              <small className="text-muted">
                Fetching positions, strikes, and live premiums...
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Suspense fallback={fallback}>
      {children}
    </Suspense>
  );
};

// Positions loading
export const PositionsLoader = ({ children }) => {
  const fallback = (
    <div className="positions-loader">
      <div className="card">
        <div className="card-header bg-success text-white">
          <h5 className="card-title mb-0">
            <i className="fas fa-briefcase me-2"></i>
            Positions
          </h5>
        </div>
        <div className="card-body">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
            <div className="text-center">
              <div className="spinner-border text-success mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <h6>Loading Positions</h6>
              <small className="text-muted">
                Calculating P&L and current values...
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Suspense fallback={fallback}>
      {children}
    </Suspense>
  );
};

// Chart/Graph loading
export const ChartLoader = ({ children, chartType = 'chart' }) => {
  const fallback = (
    <div className="chart-loader">
      <div className="d-flex justify-content-center align-items-center bg-light border rounded" style={{ minHeight: '250px' }}>
        <div className="text-center">
          <div className="spinner-border text-info mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h6>Loading {chartType}</h6>
          <small className="text-muted">Preparing visualization...</small>
        </div>
      </div>
    </div>
  );

  return (
    <Suspense fallback={fallback}>
      {children}
    </Suspense>
  );
};

// Real-time data loading
export const RealTimeLoader = ({ children, streamType = 'live data' }) => {
  const fallback = (
    <div className="realtime-loader">
      <div className="alert alert-info" role="alert">
        <div className="d-flex align-items-center">
          <div className="spinner-border spinner-border-sm text-info me-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div>
            <h6 className="alert-heading mb-1">
              <i className="fas fa-satellite-dish me-1"></i>
              Connecting to {streamType}
            </h6>
            <small className="mb-0">Establishing real-time connection...</small>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Suspense fallback={fallback}>
      {children}
    </Suspense>
  );
};

// Skeleton loader for table data
export const TableSkeletonLoader = ({ rows = 5, columns = 6 }) => {
  return (
    <div className="table-skeleton-loader">
      <div className="table-responsive">
        <table className="table table-striped">
          <thead>
            <tr>
              {Array.from({ length: columns }, (_, i) => (
                <th key={i}>
                  <div className="placeholder-glow">
                    <span className="placeholder col-8"></span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }, (_, rowIndex) => (
              <tr key={rowIndex}>
                {Array.from({ length: columns }, (_, colIndex) => (
                  <td key={colIndex}>
                    <div className="placeholder-glow">
                      <span className="placeholder col-10"></span>
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Progressive loading for large datasets
export const ProgressiveLoader = ({ children, totalItems, loadedItems }) => {
  const progress = totalItems ? Math.round((loadedItems / totalItems) * 100) : 0;

  const fallback = (
    <div className="progressive-loader">
      <div className="card">
        <div className="card-body">
          <div className="text-center mb-3">
            <h6>Loading Data...</h6>
            <div className="progress mb-2" style={{ height: '20px' }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{ width: `${progress}%` }}
                aria-valuenow={progress}
                aria-valuemin="0"
                aria-valuemax="100"
              >
                {progress}%
              </div>
            </div>
            <small className="text-muted">
              Loaded {loadedItems} of {totalItems} items
            </small>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Suspense fallback={fallback}>
      {children}
    </Suspense>
  );
};

// Higher-order component for easy Suspense wrapping
export const withSuspenseLoader = (WrappedComponent, LoaderComponent = TradingDataLoader) => {
  return React.forwardRef((props, ref) => (
    <LoaderComponent {...props}>
      <WrappedComponent {...props} ref={ref} />
    </LoaderComponent>
  ));
};

// Lazy loading wrapper with error boundary
export const LazyComponentLoader = ({ children, componentName = 'Component' }) => {
  const fallback = (
    <div className="lazy-component-loader">
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '150px' }}>
        <div className="text-center">
          <div className="spinner-border text-secondary mb-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h6 className="text-muted">Loading {componentName}...</h6>
        </div>
      </div>
    </div>
  );

  return (
    <Suspense fallback={fallback}>
      {children}
    </Suspense>
  );
};
